import { createContext, useContext, useMemo } from 'react';

import { IndicatorType } from '../blocks/molecules/indicators/create-indicators-config';

const colours = [
  'var(--color-aquamarine-500)',
  'var(--color-mauve-500)',
  'var(--color-dodger-500)',
  'var(--color-pink-500)',
];
const indicatorAppearances = ['aquamarine' as const, 'mauve' as const, 'dodger' as const, 'pink' as const];
const appearances = ['aquamarine' as const, 'mauve' as const, 'indigo' as const, 'pink' as const];

type ContextType = {
  indicators: IndicatorType[];
  allRespondents: Set<string>;
  respondents: { respondentType: string; completedAt?: string | null }[];
};
const IndicatorContext = createContext<ContextType>({ indicators: [], allRespondents: new Set(), respondents: [] });
const useIndicators = () => useContext(IndicatorContext);
type IndicatorDisplay = IndicatorType & {
  label: string;
  shortLabel: string;
  indicatorAppearance: (typeof indicatorAppearances)[number];
  appearance: (typeof appearances)[number];
  colour: string;
  disabled: boolean;
  completedAt?: string | null;
};

const createIndicatorDisplay = (
  indicatorType: IndicatorType,
  idx: number,
  respondent: { respondentIncluded: boolean; completedAt?: string | null }
): IndicatorDisplay => ({
  ...indicatorType,
  appearance: appearances[idx],
  colour: colours[idx],
  completedAt: respondent.completedAt,
  disabled: !respondent.respondentIncluded,
  indicatorAppearance: indicatorAppearances[idx],
});

const useAllIndicators = () => {
  const { indicators, allRespondents, respondents } = useIndicators();
  return useMemo(
    () =>
      indicators.map((value, index) =>
        createIndicatorDisplay(value, index, {
          respondentIncluded: allRespondents.has(value.respondentType),
          completedAt: respondents.find((r) => r.respondentType === value.respondentType)?.completedAt,
        })
      ),
    [indicators, allRespondents, respondents]
  );
};

const useIndicator = (respondentType?: string) => {
  const indicators = useAllIndicators();
  return useMemo<IndicatorDisplay | undefined>(() => {
    if (!respondentType) return undefined;
    const selectedIdx = indicators.findIndex(({ respondentType: rt }) => rt === respondentType);

    return selectedIdx !== -1 ? indicators[selectedIdx] : undefined;
  }, [respondentType, indicators]);
};

const useIndicatorColour = (respondentType?: string) => {
  const indicator = useIndicator(respondentType);
  return indicator?.colour;
};

const useIndicatorDisabled = (respondentType?: string) => {
  const indicator = useIndicator(respondentType);
  return indicator?.disabled ?? true;
};

export { IndicatorContext, useAllIndicators, useIndicator, useIndicatorColour, useIndicatorDisabled };

export type { IndicatorDisplay };
